/*
 * Copyright (c) 2022. American Express Company
 */

import React from 'react';
import { ButtonSecondary } from '@americanexpress/dls-react';

import LiteralsService from 'src/services/literals/LiteralsService';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Typography from 'src/components/typography/Typography';
import Card from 'src/components/atomic/atoms/card/Card';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import Circle from 'src/components/atomic/atoms/circle/Circle';

import { props, sectionProps } from 'src/types/view-models/getStarted.view.model';
import bgColors from 'src/styles/bgColors.module.scss';
import styles from './getStarted.module.scss';
import Selectors from 'src/utils/selectors';

const Section = ({ icon, body, button }: sectionProps): JSX.Element => (
    <>
        <Divider />
        <div className={styles.section}>
            {icon}
            <div className={styles.sectionContainer}>
                <div className={styles.bodyContainer}>{body}</div>
                {button}
            </div>
        </div>
    </>
);

export default function GetStarted({
    user,
    budgets,
    plan,
    addLifeEventOnClick,
    viewProjectionsOnClick,
    createBudgetsOnClick
}: props): JSX.Element {
    let count = 0;
    let goal1: JSX.Element = null;
    let goal2: JSX.Element = null;
    let goal3: JSX.Element = null;

    if (plan.addLifeEvent) {
        count = count + 1;
        goal1 = (
            <Section
                icon={
                    <Circle className={bgColors.backgroundHighlight}>
                        <Typography component="p" variant={TypographyVariants.HEADING_M}>
                            {count}
                        </Typography>
                    </Circle>
                }
                body={
                    <>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('Insights.getStarted.buildOut')}
                        </Typography>
                        <Typography variant={TypographyVariants.BODY}>
                            {LiteralsService.getLiteral('Insights.getStarted.personalizeYourPlan')}
                        </Typography>
                    </>
                }
                button={
                    <ButtonSecondary
                        className={styles.button}
                        onClick={addLifeEventOnClick}
                        data-cy={Selectors.PlanAhead.INSIGHTS_LIFE_EVENTS}
                    >
                        {LiteralsService.getLiteral('Insights.getStarted.addLifeEvents')}
                    </ButtonSecondary>
                }
            />
        );
    }
    if (plan.viewExpenseProjection) {
        count = count + 1;
        goal2 = (
            <Section
                icon={
                    <Circle className={bgColors.backgroundHighlight}>
                        <Typography component="p" variant={TypographyVariants.HEADING_M}>
                            {count}
                        </Typography>
                    </Circle>
                }
                body={
                    <>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('Insights.getStarted.checkOutYourIncome')}
                        </Typography>
                        <Typography variant={TypographyVariants.BODY}>
                            {LiteralsService.getLiteral('Insights.getStarted.takeALook')}
                        </Typography>
                    </>
                }
                button={
                    <ButtonSecondary
                        className={styles.button}
                        onClick={viewProjectionsOnClick}
                        data-cy={Selectors.PlanAhead.INSIGHTS_VIEW_PROJECTIONS}
                    >
                        {LiteralsService.getLiteral('Insights.getStarted.viewProjections')}
                    </ButtonSecondary>
                }
            />
        );
    }

    if (!budgets.length) {
        count = count + 1;
        goal3 = (
            <Section
                icon={
                    <Circle className={bgColors.backgroundHighlight}>
                        <Typography component="p" variant={TypographyVariants.HEADING_M}>
                            {count}
                        </Typography>
                    </Circle>
                }
                body={
                    <>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {LiteralsService.getLiteral('Insights.getStarted.addBudget')}
                        </Typography>
                        <Typography variant={TypographyVariants.BODY}>
                            {LiteralsService.getLiteral('Insights.getStarted.setupBudget')}
                        </Typography>
                    </>
                }
                button={
                    <ButtonSecondary
                        className={styles.button}
                        onClick={createBudgetsOnClick}
                        data-cy={Selectors.PlanAhead.INSIGHTS_ADD_ACCOUNT}
                    >
                        {LiteralsService.getLiteral('Insights.getStarted.createBudget')}
                    </ButtonSecondary>
                }
            />
        );
    }

    return (
        <Card>
            <Typography component="h2" variant={TypographyVariants.HEADING_XS}>
                {LiteralsService.getLiteral('Insights.getStarted.getStarted')}
            </Typography>
            <Typography variant={TypographyVariants.CAPTION}>
                {LiteralsService.getLiteral('Insights.getStarted.learnAbout')}
            </Typography>
            {goal1}
            {goal2}
            {goal3}
        </Card>
    );
}
